import type { ApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { RequestBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";
import {
  getDocumentProcessingRunForUploadJobItem,
  parseLocaleNumber,
  type DocumentProcessingRun,
  type DocumentProcessingRunLookup,
} from "./documentProcessingRun";
import {
  findDocumentValues,
  planDocumentValueConfirmation,
  type DocumentValueDraft,
} from "./documentValues";
import { createUploadJobIdempotencyKey } from "./uploadJobActivity";

type ReviewRequest = RequestBody<
  paths["/patients/{patientId}/document-processing-runs/{runId}/reviews"]["post"]
>;
type ObservationRequest = RequestBody<
  paths["/patients/{patientId}/clinical-observations"]["post"]
>;
type PromotionProposal =
  paths["/patients/{patientId}/document-processing-runs/{runId}/promotion-proposals"]["post"]["responses"][200]["content"]["application/json"]["data"]["proposals"][number];

const UPLOAD_JOB_PAGE_LIMIT = 100;
const UPLOAD_JOB_MAX_PAGES = 20;

/**
 * The processing lookup behind a saved document, found through the upload
 * job item that acknowledged one of its files. A document carries no run id
 * of its own; its uploads are the link the API exposes.
 *
 * Returns the ready lookup when one exists, otherwise the first lookup found
 * (still being read), otherwise null for a document no upload job produced.
 */
export async function findDocumentProcessingLookup(
  client: ApiClient,
  patientId: string,
  uploadIds: readonly string[],
): Promise<DocumentProcessingRunLookup | null> {
  if (uploadIds.length === 0) return null;
  const wanted = new Set(uploadIds);
  const items: { jobId: string; fileItemId: string; updatedAt: string }[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < UPLOAD_JOB_MAX_PAGES; page += 1) {
    const { data, error, response } = await client.GET(
      "/patients/{patientId}/upload-jobs",
      {
        params: {
          path: { patientId },
          query: {
            includeCompleted: true,
            limit: UPLOAD_JOB_PAGE_LIMIT,
            ...(cursor ? { cursor } : {}),
          },
        },
      },
    );
    if (!data) {
      throwApiError(
        error,
        "Could not find how this document was read",
        "findDocumentProcessingLookup",
        response?.status,
      );
    }
    for (const job of data.data.jobs) {
      for (const item of job.items) {
        if (item.uploadId && wanted.has(item.uploadId)) {
          items.push({
            jobId: job.id,
            fileItemId: item.id,
            updatedAt: item.updatedAt,
          });
        }
      }
    }
    if (!data.data.nextCursor) break;
    cursor = data.data.nextCursor;
  }

  items.sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  let pending: DocumentProcessingRunLookup | null = null;
  for (const item of items) {
    const lookup = await getDocumentProcessingRunForUploadJobItem(
      client,
      patientId,
      item.jobId,
      item.fileItemId,
    );
    if (lookup.state === "ready" && lookup.run) return lookup;
    pending ??= lookup;
  }
  return pending;
}

export async function getDocumentProcessingRun(
  client: ApiClient,
  patientId: string,
  runId: string,
): Promise<DocumentProcessingRun> {
  const { data, error, response } = await client.GET(
    "/patients/{patientId}/document-processing-runs/{runId}",
    { params: { path: { patientId, runId } } },
  );
  if (!data) {
    throwApiError(
      error,
      "Could not read the document's values",
      "getDocumentProcessingRun",
      response?.status,
    );
  }
  return data.data;
}

async function reviewDocumentProcessingClaims(
  client: ApiClient,
  patientId: string,
  runId: string,
  reviews: ReviewRequest["reviews"],
): Promise<DocumentProcessingRun> {
  const { data, error, response } = await client.POST(
    "/patients/{patientId}/document-processing-runs/{runId}/reviews",
    {
      params: { path: { patientId, runId } },
      body: { clientRequestId: createUploadJobIdempotencyKey(), reviews },
    },
  );
  if (!data) {
    throwApiError(
      error,
      "Could not confirm the document's values",
      "reviewDocumentProcessingClaims",
      response?.status,
    );
  }
  return data.data.run;
}

async function prepareDocumentPromotionProposals(
  client: ApiClient,
  patientId: string,
  runId: string,
  capabilities: string[],
): Promise<PromotionProposal[]> {
  const { data, error, response } = await client.POST(
    "/patients/{patientId}/document-processing-runs/{runId}/promotion-proposals",
    {
      params: { path: { patientId, runId } },
      body: { clientRequestId: createUploadJobIdempotencyKey(), capabilities },
    },
  );
  if (!data) {
    throwApiError(
      error,
      "Could not prepare the document's values for saving",
      "prepareDocumentPromotionProposals",
      response?.status,
    );
  }
  return data.data.proposals;
}

async function promoteDocumentPromotionProposal(
  client: ApiClient,
  patientId: string,
  runId: string,
  proposalId: string,
): Promise<PromotionProposal | undefined> {
  const { data, error, response } = await client.POST(
    "/patients/{patientId}/document-processing-runs/{runId}/promotion-proposals/{proposalId}/promote",
    {
      params: { path: { patientId, runId, proposalId } },
      body: { clientRequestId: createUploadJobIdempotencyKey() },
    },
  );
  if (!data) {
    throwApiError(
      error,
      "Could not save the document's values",
      "promoteDocumentPromotionProposal",
      response?.status,
    );
  }
  return data.data.proposals.find((proposal) => proposal.id === proposalId);
}

/** A value with the idempotency key it keeps across retries. */
export type DocumentValueToRecord = DocumentValueDraft & {
  clientRequestId: string;
};

function todayIsoDate(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * A patient-entered value written as a clinical observation, with the
 * document as its evidence, so it is listed with the document's values and
 * with the patient's results.
 */
export function documentValueObservationRequest(
  value: DocumentValueToRecord,
  sourceEntityId: string,
): ObservationRequest {
  const figure = parseLocaleNumber(value.value);
  const unit = value.unit.trim();
  const date = value.date ?? todayIsoDate();
  return {
    clientRequestId: value.clientRequestId,
    rawLabel: value.name.trim(),
    valueKind: figure === null ? "text" : "quantity",
    terminology: {
      assertions: [],
      states: [
        {
          purpose: "clinical",
          status: "needs_review",
          reason:
            "Patient-entered value from an uploaded document requires clinical coding review",
        },
      ],
    },
    typedValue:
      figure === null
        ? {
            kind: "text",
            originalValue: value.value.trim(),
            parsedValue: { value: value.value.trim() },
          }
        : {
            kind: "quantity",
            quantityRole: "measurement",
            originalValue: String(figure),
            originalUnit: unit || null,
            parsedValue: { value: String(figure) },
          },
    effectiveAt: `${date}T00:00:00.000Z`,
    datePrecision: "day",
    evidenceEntityIds: [sourceEntityId],
  };
}

async function recordDocumentValue(
  client: ApiClient,
  patientId: string,
  sourceEntityId: string,
  value: DocumentValueToRecord,
): Promise<void> {
  const { data, error, response } = await client.POST(
    "/patients/{patientId}/clinical-observations",
    {
      params: { path: { patientId } },
      body: documentValueObservationRequest(value, sourceEntityId),
    },
  );
  if (!data) {
    throwApiError(
      error,
      "Could not save the value",
      "recordDocumentValue",
      response?.status,
    );
  }
}

const PROMOTABLE_STATUSES = new Set(["proposed", "failed"]);

/**
 * Promote the run's accepted values through its own proposals. Proposals
 * already promoted are left alone, so this is safe to repeat after a failure.
 */
async function promoteConfirmedDocumentValues(
  client: ApiClient,
  patientId: string,
  run: DocumentProcessingRun,
  documentDate: string | null,
): Promise<void> {
  const confirmed = findDocumentValues(run, documentDate).filter(
    (value) => value.saved && value.route !== "record",
  );
  const capabilities = [
    ...new Set(
      confirmed.flatMap((value) =>
        value.route === "record" ? [] : [value.route],
      ),
    ),
  ];
  if (capabilities.length === 0) return;

  const confirmedClaimIds = new Set(confirmed.map((value) => value.claimId));
  const acceptedClaimIds = new Set(
    run.claims
      .filter((claim) => claim.status === "accepted")
      .map((claim) => claim.id),
  );
  const proposals = await prepareDocumentPromotionProposals(
    client,
    patientId,
    run.id,
    capabilities,
  );
  for (const proposal of proposals) {
    if (
      !capabilities.some((code) => code === proposal.capability) ||
      !PROMOTABLE_STATUSES.has(proposal.status) ||
      proposal.claimIds.length === 0 ||
      !proposal.claimIds.every((claimId) => acceptedClaimIds.has(claimId)) ||
      !proposal.claimIds.some((claimId) => confirmedClaimIds.has(claimId))
    ) {
      continue;
    }
    const promoted = await promoteDocumentPromotionProposal(
      client,
      patientId,
      run.id,
      proposal.id,
    );
    if (promoted && promoted.status !== "promoted") {
      throw new Error(
        promoted.statusReason ?? "The document's values were not saved",
      );
    }
  }
}

/**
 * Confirm a document's values: review every value still waiting, promote the
 * accepted ones through the run's own proposals, and record the rest as
 * patient-entered observations linked to the document.
 *
 * The run is read fresh first, so a retry after a partial failure reviews
 * only what is still waiting and promotes what was accepted but not filed.
 * Corrections are handed back through `onReviewed` once their claims are
 * rejected; the caller keeps them with the values it records, under the same
 * idempotency key, so a retry records each one exactly once.
 */
export async function confirmDocumentValues(
  client: ApiClient,
  patientId: string,
  input: {
    /** Null while the document is still being read: values are only added. */
    runId: string | null;
    /** The document's evidence entity, which recorded values link to. */
    sourceEntityId: string;
    documentDate: string | null;
    edits: Readonly<Record<string, DocumentValueDraft>>;
    removedClaimIds: ReadonlySet<string>;
    /** Values added by hand, and corrections kept from an earlier attempt. */
    recorded: readonly DocumentValueToRecord[];
    onReviewed?: (
      corrections: DocumentValueToRecord[],
      run: DocumentProcessingRun,
    ) => void;
    onRecorded?: (clientRequestId: string) => void;
  },
): Promise<DocumentProcessingRun | null> {
  let run = input.runId
    ? await getDocumentProcessingRun(client, patientId, input.runId)
    : null;
  let corrections: DocumentValueToRecord[] = [];

  if (run) {
    const plan = planDocumentValueConfirmation({
      run,
      documentDate: input.documentDate,
      edits: input.edits,
      removedClaimIds: input.removedClaimIds,
      added: [],
    });
    if (plan.reviews.length > 0) {
      corrections = plan.record.map((draft) => ({
        ...draft,
        clientRequestId: createUploadJobIdempotencyKey(),
      }));
      run = await reviewDocumentProcessingClaims(
        client,
        patientId,
        run.id,
        plan.reviews.map((review) => ({
          claimId: review.claimId,
          expectedVersion: review.expectedVersion,
          decision: review.decision,
          reason: review.reason,
          ...(review.normalizedValue
            ? { normalizedValue: review.normalizedValue }
            : {}),
        })),
      );
      input.onReviewed?.(corrections, run);
    }
    await promoteConfirmedDocumentValues(
      client,
      patientId,
      run,
      input.documentDate,
    );
  }

  for (const value of [...input.recorded, ...corrections]) {
    await recordDocumentValue(client, patientId, input.sourceEntityId, value);
    input.onRecorded?.(value.clientRequestId);
  }

  return run;
}
